import { useAuth } from "@/context/AuthProvider";
import { cn } from "@/lib/utils";
import { CampanhaService, type CampanhaDetalhes } from "@/services/app/campanhas";
import { PetsService, type PetData } from "@/services/app/pets";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    AlertCircle,
    ArrowLeft,
    Calendar,
    CheckCircle,
    Clock,
    Megaphone,
    PawPrint,
    Users,
} from "lucide-react";
import moment from "moment";
import React, { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

function DetalhesCampanha(): React.JSX.Element {
  const { campanhaId } = useParams<{ campanhaId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [petSelecionado, setPetSelecionado] = useState<string>("");
  const [observacao, setObservacao] = useState("");
  const [mostrarFormulario, setMostrarFormulario] = useState(false);

  const campanhaService = new CampanhaService();
  const petsService = new PetsService();

  // Query para buscar detalhes da campanha
  const {
    data: campanha,
    isLoading: carregandoCampanha,
    error: erroCampanha,
  } = useQuery<CampanhaDetalhes>({
    queryKey: ["campanha", campanhaId],
    queryFn: async () => {
      if (!campanhaId) throw new Error("ID da campanha não fornecido");
      return await campanhaService.getCampanhaById(campanhaId);
    },
    enabled: !!campanhaId,
  });

  // Query para buscar pets do usuário
  const { data: pets } = useQuery<PetData[]>({
    queryKey: ["pets", user?.id],
    queryFn: async () => {
      return await petsService.getPets();
    },
    enabled: !!user?.id && mostrarFormulario,
  });

  // Mutation para agendar
  const agendarMutation = useMutation({
    mutationFn: async () => {
      if (!campanhaId || !petSelecionado || !campanha) {
        throw new Error("Dados incompletos para agendamento");
      }

      return await campanhaService.agendarParaCampanha({
        petId: petSelecionado,
        tipoId: campanha.tipo.id,
        observacao: observacao || "Nenhuma observação",
        campanhaId: campanhaId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campanha", campanhaId] });
      queryClient.invalidateQueries({ queryKey: ["campanhas-ativas"] });
      setMostrarFormulario(false);
      setPetSelecionado("");
      setObservacao("");
      toast.success("Pet inscrito na campanha com sucesso!", {
        description: "Você receberá mais informações em breve sobre a campanha.",
      });
    },
    onError: (error: unknown) => {
      console.error("Erro ao inscrever pet na campanha:", error);
      
      // Tratamento de erros mais específico
      let errorMessage = "Erro ao inscrever pet na campanha";
      
      // Verifica se é um erro do Axios
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
        
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.response?.status === 400) {
          errorMessage = "Dados inválidos para inscrição";
        } else if (axiosError.response?.status === 409) {
          errorMessage = "Pet já inscrito nesta campanha";
        } else if (axiosError.response?.status === 422) {
          errorMessage = "Não é possível inscrever este pet na campanha";
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    },
  });



  const formatDate = useCallback((dateString: string): string => {
    try {
      return moment(dateString).format("DD [de] MMMM [de] YYYY");
    } catch {
      return "Data inválida";
    }
  }, []);

  const formatDateTime = useCallback((dateString: string): string => {
    try {
      return moment(dateString).format("DD/MM/YYYY [às] HH:mm");
    } catch {
      return "Data inválida";
    }
  }, []);

  const isInscricaoAberta = useCallback((dataFimInscricao: string): boolean => {
    const now = new Date();
    const fimInscricao = new Date(dataFimInscricao);
    return now <= fimInscricao;
  }, []);

  const handleAgendar = useCallback(() => {
    if (!petSelecionado) {
      toast.warning("Por favor, selecione um pet para inscrever na campanha.");
      return;
    }
    agendarMutation.mutate();
  }, [petSelecionado, agendarMutation]);

  const handleVoltar = useCallback(() => {
    navigate("/campanhas");
  }, [navigate]);

  const handleMostrarFormulario = useCallback(() => {
    setMostrarFormulario(true);
  }, []);

  const handleCancelarFormulario = useCallback(() => {
    setMostrarFormulario(false);
  }, []);

  const handlePetChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setPetSelecionado(e.target.value);
  }, []);

  const handleObservacaoChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setObservacao(e.target.value);
  }, []);

  if (carregandoCampanha) {
    return (
      <div className="space-y-6">
        {/* Header com botão de voltar */}
        <button
          onClick={handleVoltar}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 transition-colors"
        >
          <ArrowLeft size={20} />
          Voltar para Campanhas
        </button>

        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-32 bg-gray-200 rounded mb-6"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (erroCampanha || !campanha) {
    return (
      <div className="space-y-6">
        {/* Header com botão de voltar */}
        <button
          onClick={handleVoltar}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 transition-colors"
        >
          <ArrowLeft size={20} />
          Voltar para Campanhas
        </button>

        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <h3 className="text-lg font-medium text-red-800 mb-2">
            Erro ao carregar campanha
          </h3>
          <p className="text-red-600">
            Não foi possível carregar os detalhes da campanha.
          </p>
        </div>
      </div>
    );
  }

  const inscricaoAberta = isInscricaoAberta(campanha.dataFimInscricao);
  const temVagas = campanha.vagasDisponiveis > 0;
  const percentualOcupacao = Math.round(
    ((campanha.vagasTotal - campanha.vagasDisponiveis) / campanha.vagasTotal) * 100
  );

  return (
    <div className="space-y-6">
      {/* Header com botão de voltar */}
      <button
        onClick={handleVoltar}
        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 transition-colors mb-4"
      >
        <ArrowLeft size={20} />
        Voltar para Campanhas
      </button>

      {/* Card principal da campanha */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          <Megaphone className="text-indigo-600" size={24} />
          <div className="flex gap-2">
            <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-1 rounded-full">
              {campanha.tipo.nome}
            </span>
            {inscricaoAberta ? (
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                Inscrições Abertas
              </span>
            ) : (
              <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                Inscrições Encerradas
              </span>
            )}
          </div>
        </div>

        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          {campanha.nome}
        </h1>

        <p className="text-gray-600 leading-relaxed mb-6">
          {campanha.descricao}
        </p>

        {/* Informações detalhadas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Calendar size={20} className="text-indigo-500" />
              <div>
                <span className="text-sm text-gray-500">Data do evento:</span>
                <p className="font-medium text-gray-800">
                  {formatDate(campanha.dataEvento)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Clock size={20} className="text-orange-500" />
              <div>
                <span className="text-sm text-gray-500">
                  Período de inscrições:
                </span>
                <p className="font-medium text-gray-800">
                  {formatDateTime(campanha.dataInicioInscricao)} até{" "}
                  {formatDateTime(campanha.dataFimInscricao)}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Users size={20} className="text-green-500" />
              <div>
                <span className="text-sm text-gray-500">Vagas:</span>
                <p className="font-medium text-gray-800">
                  {campanha.vagasDisponiveis} disponíveis de {campanha.vagasTotal}
                </p>
              </div>
            </div>

            {/* Barra de progresso */}
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Vagas ocupadas</span>
                <span>{percentualOcupacao}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-indigo-600 h-3 rounded-full transition-all duration-300"
                  style={{
                    width: `${percentualOcupacao}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Botão principal de ação */}
        {!mostrarFormulario && (
          <button
            onClick={handleMostrarFormulario}
            disabled={!inscricaoAberta || !temVagas}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {!inscricaoAberta ? (
              "Inscrições Encerradas"
            ) : !temVagas ? (
              "Vagas Esgotadas"
            ) : (
              <>
                <PawPrint size={20} />
                Inscrever Meu Pet
              </>
            )}
          </button>
        )}
      </div>

      {/* Formulário de agendamento */}
      {mostrarFormulario && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Inscrever Pet na Campanha
          </h3>

          <div className="space-y-4">
            {/* Seleção de pet */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selecione o pet:
              </label>
              <select
                value={petSelecionado}
                onChange={handlePetChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Escolha um pet</option>
                {pets?.map((pet) => (
                  <option key={pet.id} value={pet.id} className="capitalize">
                    {pet.nome.charAt(0).toUpperCase() + pet.nome.slice(1)} - {pet.especie === 'CACHORRO' ? 'Cão' : 'Gato'}
                  </option>
                ))}
              </select>
            </div>

            {/* Observações */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações (opcional):
              </label>
              <textarea
                value={observacao}
                onChange={handleObservacaoChange}
                placeholder="Alguma informação adicional sobre seu pet..."
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Botões de ação */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleCancelarFormulario}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAgendar}
                disabled={agendarMutation.isPending || !petSelecionado}
                className={cn(
                  "flex-1 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2",
                  agendarMutation.isPending || !petSelecionado
                    ? "bg-gray-300 cursor-not-allowed text-gray-500"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white"
                )}
              >
                {agendarMutation.isPending ? (
                  "Inscrevendo..."
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Confirmar Inscrição
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default DetalhesCampanha;
